import {
  COMPACT_MAP_HEIGHT,
  COMPACT_MAP_WIDTH,
  COMPACT_OVERVIEW_MARKER_LIMIT,
  COMPACT_REGIONAL_MARKER_LIMIT,
  DETAIL_TO_REGIONAL_ZOOM,
  DETAIL_ZOOM_SCALE,
  MAX_OVERVIEW_MARKER_LIMIT,
  MAX_REGIONAL_MARKER_LIMIT,
  OVERVIEW_MARKER_AREA,
  OVERVIEW_MARKER_LIMIT,
  OVERVIEW_TO_REGIONAL_ZOOM,
  REGIONAL_MARKER_AREA,
  REGIONAL_MARKER_LIMIT,
  REGIONAL_TO_DETAIL_ZOOM,
  REGIONAL_TO_OVERVIEW_ZOOM,
  REGIONAL_ZOOM_SCALE,
} from './constants';
import type { MarkerDensity, MarkerPolicy, MarkerViewport } from './types';

export function getMarkerPolicy(
  zoomScale: number,
  viewport?: MarkerViewport
): MarkerPolicy {
  const isCompact = isCompactViewport(viewport);

  if (!Number.isFinite(zoomScale) || zoomScale < REGIONAL_ZOOM_SCALE) {
    return {
      kind: 'overview',
      maxMarkers: getResponsiveMarkerLimit(
        isCompact ? COMPACT_OVERVIEW_MARKER_LIMIT : OVERVIEW_MARKER_LIMIT,
        isCompact ? COMPACT_OVERVIEW_MARKER_LIMIT : MAX_OVERVIEW_MARKER_LIMIT,
        viewport,
        OVERVIEW_MARKER_AREA
      ),
    };
  }

  if (zoomScale < DETAIL_ZOOM_SCALE) {
    return {
      kind: 'regional',
      maxMarkers: getResponsiveMarkerLimit(
        isCompact ? COMPACT_REGIONAL_MARKER_LIMIT : REGIONAL_MARKER_LIMIT,
        isCompact ? COMPACT_REGIONAL_MARKER_LIMIT : MAX_REGIONAL_MARKER_LIMIT,
        viewport,
        REGIONAL_MARKER_AREA
      ),
    };
  }

  return {
    kind: 'detail',
    maxMarkers: isCompact ? 6 : 36,
  };
}

function getPolicyForDensity(
  density: MarkerDensity,
  viewport: MarkerViewport
): MarkerPolicy {
  if (density === 'overview') return getMarkerPolicy(1, viewport);
  if (density === 'regional') return getMarkerPolicy(2, viewport);
  return getMarkerPolicy(3, viewport);
}

export function getSettledMarkerPolicy(
  zoomScale: number,
  viewport: MarkerViewport,
  previousDensity?: MarkerDensity
): MarkerPolicy {
  if (!previousDensity) return getMarkerPolicy(zoomScale, viewport);

  if (previousDensity === 'overview' && zoomScale < OVERVIEW_TO_REGIONAL_ZOOM) {
    return getPolicyForDensity('overview', viewport);
  }

  if (
    previousDensity === 'regional' &&
    zoomScale >= REGIONAL_TO_OVERVIEW_ZOOM &&
    zoomScale < REGIONAL_TO_DETAIL_ZOOM
  ) {
    return getPolicyForDensity('regional', viewport);
  }

  if (previousDensity === 'detail' && zoomScale >= DETAIL_TO_REGIONAL_ZOOM) {
    return getPolicyForDensity('detail', viewport);
  }

  return getMarkerPolicy(zoomScale, viewport);
}

function getResponsiveMarkerLimit(
  baseLimit: number,
  maximumLimit: number,
  viewport: MarkerViewport | undefined,
  areaPerMarker: number
): number {
  if (
    !viewport ||
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return baseLimit;
  }

  const areaCapacity = Math.floor(
    (viewport.width * viewport.height) / areaPerMarker
  );

  return Math.min(maximumLimit, Math.max(baseLimit, areaCapacity));
}

export function isCompactViewport(
  viewport: MarkerViewport | undefined
): boolean {
  return Boolean(
    viewport &&
    Number.isFinite(viewport.width) &&
    viewport.width > 0 &&
    (viewport.width < COMPACT_MAP_WIDTH ||
      (Number.isFinite(viewport.height) &&
        viewport.height > 0 &&
        viewport.height < COMPACT_MAP_HEIGHT))
  );
}
